// Shared arcade look for every cabinet game in the arcade hall: the CRT
// bezel around the full-screen game, the top HUD (SCORE / WAVE / LIVES),
// the neon question box, numbered answer panels and the EXIT control.
// Games own their playfield; everything around it comes from here so all
// cabinets read as one machine family. Sizes inside the glass use container
// units, so the glass element must set `container-type: size` (class
// `arc-glass` does).

import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import "./arcade.css";
import { tx } from "../../lib/tx";

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function useViewport() {
  const [vp, setVp] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const on = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return vp;
}

/**
 * Glass (the 16:9 game screen) and frame (the bezel around it) for the
 * full-screen state. Side art panels need room left and right; on narrow
 * screens they shrink away and the glass takes the width.
 */
export function bigRects(vp: { w: number; h: number }, aspect = 16 / 9): { glass: Rect; frame: Rect; side: number } {
  const rim = Math.round(Math.max(10, Math.min(vp.w, vp.h) * 0.028));
  const side = vp.w / vp.h > 1.5 ? Math.round(Math.min(vp.w * 0.06, 110)) : 0;
  const availW = vp.w - 2 * (rim + side) - 16;
  const availH = vp.h - 2 * rim - 16;
  const s = Math.min(availW / aspect, availH);
  const w = Math.floor(s * aspect);
  const h = Math.floor(s);
  const left = Math.floor((vp.w - w) / 2);
  const top = Math.floor((vp.h - h) / 2);
  return {
    glass: { left, top, width: w, height: h },
    frame: { left: left - rim - side, top: top - rim, width: w + 2 * (rim + side), height: h + 2 * rim },
    side,
  };
}

/** Rect of the 3D cabinet screen (or a centred fallback) for the small state. */
export function cabinetRect(
  vp: { w: number; h: number },
  box: { leftPct: number; topPct: number; widthPct: number; heightPct: number; visible: boolean } | null,
  aspect = 1.22,
): Rect {
  if (box && box.visible && box.widthPct > 2 && box.heightPct > 2) {
    return {
      left: (box.leftPct / 100) * vp.w,
      top: (box.topPct / 100) * vp.h,
      width: (box.widthPct / 100) * vp.w,
      height: (box.heightPct / 100) * vp.h,
    };
  }
  const w = Math.min(vp.w * 0.44, vp.h * 0.62 * aspect);
  const h = w / aspect;
  return { left: (vp.w - w) / 2, top: (vp.h - h) / 2 - vp.h * 0.04, width: w, height: h };
}

/** Decorative cabinet frame drawn around the full-screen glass. */
export function ArcadeBezel({ frame, side, visible }: { frame: Rect; side: number; visible: boolean }) {
  return (
    <div
      className={`arc-bezel${visible ? " is-on" : ""}`}
      style={{ left: frame.left, top: frame.top, width: frame.width, height: frame.height }}
      aria-hidden="true"
    >
      {side > 0 && (
        <>
          <div className="arc-side arc-side--l" style={{ width: side }} />
          <div className="arc-side arc-side--r" style={{ width: side }} />
        </>
      )}
      <div className="arc-rim" style={{ left: side, right: side }} />
    </div>
  );
}

/** CRT scanlines + vignette over the glass. */
export function CrtOverlay() {
  return <div className="arc-crt" aria-hidden="true" />;
}

export function ShipIcon({ on = true }: { on?: boolean }) {
  const px = useMemo(
    () => [
      [4, 0],
      [3, 1], [4, 1], [5, 1],
      [3, 2], [4, 2], [5, 2],
      [0, 3], [1, 3], [3, 3], [4, 3], [5, 3], [7, 3], [8, 3],
      [0, 4], [1, 4], [2, 4], [3, 4], [4, 4], [5, 4], [6, 4], [7, 4], [8, 4],
      [0, 5], [1, 5], [2, 5], [3, 5], [4, 5], [5, 5], [6, 5], [7, 5], [8, 5],
      [0, 6], [2, 6], [6, 6], [8, 6],
    ],
    [],
  );
  return (
    <svg className={`arc-ship${on ? "" : " is-lost"}`} viewBox="0 0 9 7" aria-hidden="true">
      {px.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width={1.02} height={1.02} />
      ))}
    </svg>
  );
}

const pad = (n: number, w: number) => String(Math.max(0, Math.floor(n))).padStart(w, "0");

export interface HudProps {
  score: number;
  digits?: number;
  /** Middle item, e.g. { label: "WAVE", value: "2/5" }. */
  wave?: { label: string; value: string };
  lives?: { n: number; max: number };
  /** Extra controls on the far right (sound, hint...). */
  extra?: ReactNode;
}

/** Top HUD row: SCORE (white label, green digits), WAVE (cyan), LIVES (ships). */
export function ArcadeHud({ score, digits = 6, wave, lives, extra }: HudProps) {
  return (
    <div className="arc-hud">
      <div className="arc-hud__item">
        <span className="arc-hud__l">{tx("SCORE")}</span>
        <span className="arc-hud__score">{pad(score, digits)}</span>
      </div>
      {wave && (
        <div className="arc-hud__item arc-hud__wave">
          <span>{wave.label}</span>
          <span>{wave.value}</span>
        </div>
      )}
      <div className="arc-hud__item arc-hud__right">
        {lives && (
          <span className="arc-hud__lives" aria-label={tx("{n} of {max} lives", { n: lives.n, max: lives.max })}>
            <span className="arc-hud__l">{tx("LIVES")}</span>
            {Array.from({ length: lives.max }, (_, i) => (
              <ShipIcon key={i} on={i < lives.n} />
            ))}
          </span>
        )}
        {extra}
      </div>
    </div>
  );
}

/** Centred question box: cyan neon border, yellow title, white subtitle. */
export function QuestionBox({ title, subtitle, children }: { title: string; subtitle?: string; children?: ReactNode }) {
  return (
    <div className="arc-qbox" role="status">
      <div className="arc-qbox__t">{title}</div>
      {subtitle && <div className="arc-qbox__s">{subtitle}</div>}
      {children}
    </div>
  );
}

export type PanelState = "" | "correct" | "wrong" | "dim" | "picked";

/** Numbered answer panels along the bottom of the glass. */
export function AnswerPanels({
  items,
  state,
  onPick,
}: {
  items: { key: string; label: string }[];
  state?: (i: number) => PanelState;
  onPick?: (i: number) => void;
}) {
  return (
    <div className="arc-answers" style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}>
      {items.map((it, i) => {
        const s = state?.(i) ?? "";
        const cls = `arc-ans${s ? ` is-${s}` : ""}${onPick ? " is-btn" : ""}`;
        const inner = (
          <>
            <span className="arc-ans__n">{i + 1}</span>
            <span className="arc-ans__t">{it.label}</span>
          </>
        );
        return onPick ? (
          <button key={it.key} type="button" className={cls} onClick={() => onPick(i)}>
            {inner}
          </button>
        ) : (
          <div key={it.key} className={cls}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}

/** Neon card used for menus, results and end screens. */
export function ArcadePanel({ children, wide, className }: { children: ReactNode; wide?: boolean; className?: string }) {
  return (
    <div className="arc-panel-wrap">
      <div className={`arc-panel${wide ? " is-wide" : ""}${className ? ` ${className}` : ""}`}>{children}</div>
    </div>
  );
}

export function ArcadeButton({
  children,
  onClick,
  primary,
  disabled,
  title,
}: {
  children: ReactNode;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      className={`arc-btn${primary ? " is-primary" : ""}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}

/** EXIT control, top-left inside the bezel. Leaves the cabinet at any time. */
export function ExitButton({ onExit, label = "EXIT" }: { onExit: () => void; label?: string }) {
  return (
    <button type="button" className="arc-exit" onClick={onExit} title={tx("Leave this cabinet (Esc)")}>
      <span aria-hidden="true">&#x2190;</span> {label} <kbd>{tx("ESC")}</kbd>
    </button>
  );
}

/** Escape leaves the cabinet. Capture phase so the walk controls never see it. */
export function useEscapeExit(onExit: () => void, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const on = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      onExit();
    };
    window.addEventListener("keydown", on, true);
    return () => window.removeEventListener("keydown", on, true);
  }, [onExit, enabled]);
}

export { pad as padScore };
